<script lang="ts">
  /**
   * One project of a board on a phone (ma-inzp): emoji, title, "Status · n/m
   * tasks", a thin progress bar and a chevron. The whole row is the tap target
   * and opens the project rail (a sheet on phones). Task counts follow the shared
   * task cache, so completing a task in the rail updates the row.
   */
  import type { Project } from '../../lib/types';
  import { categoryIcons } from '../../lib/utils';
  import { getTaskCounts, subscribeToProjectTaskCounts } from '../../lib/useProjectTaskCounts';
  import { openProjectRail } from '../../lib/stores/ui';

  interface Props {
    project: Project;
  }

  let { project }: Props = $props();

  // svelte-ignore state_referenced_locally
  let counts = $state(getTaskCounts(project));

  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: projectTasksCache pub/sub, re-subscribes on project change, returns cleanup
  $effect(() => subscribeToProjectTaskCounts(project, (next) => (counts = next)));

  let statusLabel = $derived(project.status.charAt(0).toUpperCase() + project.status.slice(1));
  let taskSummary = $derived(
    counts.total > 0 ? `${counts.done}/${counts.total} tasks` : 'No tasks',
  );
  let pct = $derived(counts.total > 0 ? Math.round((counts.done / counts.total) * 100) : 0);
</script>

<button type="button" class="row" onclick={() => openProjectRail(project.id)}>
  <span class="emoji" aria-hidden="true">{categoryIcons[project.category] ?? '📋'}</span>
  <span class="main">
    <span class="title">{project.title}</span>
    <span class="sub">{statusLabel} · {taskSummary}</span>
  </span>
  {#if counts.total > 0}
    <span class="bar" aria-hidden="true">
      <span class="fill" class:complete={pct === 100} style="width: {pct}%"></span>
    </span>
  {/if}
  <span class="chev" aria-hidden="true">›</span>
</button>

<style>
  .row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 56px;
    padding: 8px 12px 8px 16px;
    border: 0;
    background: none;
    font: inherit;
    color: #1f2937;
    text-align: left;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .row:active {
    background: #f9fafb;
  }

  .emoji {
    flex: 0 0 auto;
    font-size: 20px;
    line-height: 1;
  }

  .main {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .title {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.25;
    overflow-wrap: anywhere;
  }

  .sub {
    font-size: 12px;
    color: #6b7280;
    font-variant-numeric: tabular-nums;
  }

  .bar {
    flex: 0 0 60px;
    height: 4px;
    border-radius: 2px;
    background: #e5e7eb;
    overflow: hidden;
  }

  .fill {
    display: block;
    height: 100%;
    background: #3b82f6;
  }

  .fill.complete {
    background: #22c55e;
  }

  .chev {
    flex: 0 0 auto;
    font-size: 20px;
    line-height: 1;
    color: #d1d5db;
  }
</style>
